import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type FC, useCallback } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog'
import { buttonVariants } from '@/components/button/variant'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { deleteCasesMutation } from '@/lib/queries/business/v2/mutations'

export interface Props {
  open: boolean
  caseIds: number[]
  showAssociatedCasesWarningText?: boolean
  onClose: () => void
}

export const DeleteCasesDialog: FC<Props> = ({ open, caseIds, showAssociatedCasesWarningText = true, onClose }) => {
  const queryClient = useQueryClient()
  const mutationDelete = useMutation(deleteCasesMutation)

  const handleDelete = useCallback(() => {
    mutationDelete.mutate(
      { caseIds: caseIds },
      {
        onSuccess: async () => {
          onClose()
          toast.success('Cases deleted successfully.')
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v4.case.search.index],
          })
        },
        onError: () => {
          toast.error('Failed to delete cases.')
        },
      }
    )
  }, [caseIds, mutationDelete, onClose, queryClient])

  const isPlural = caseIds.length > 1

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Deleting {caseIds.length} {isPlural ? 'cases' : 'case'}
          </AlertDialogTitle>

          <AlertDialogDescription>
            Are you sure you want to delete {isPlural ? 'these cases' : 'this case'}?{' '}
            {showAssociatedCasesWarningText &&
              `${isPlural ? 'They' : 'It'} will also be removed from the case list of the associated ${isPlural ? 'applicants' : 'applicant'}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>

          <AlertDialogAction
            className={buttonVariants({ variant: 'destructive' })}
            disabled={mutationDelete.isPending}
            onClick={handleDelete}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
